import {
  BASE_COLORS,
  CHART_PALETTES,
  FONTS,
  RADII_LIST,
  STYLES,
  THEMES,
} from "@cogo/design-presets";

import type { DesignSystemGenerationContract } from "./types";

export const DESIGN_SYSTEM_GENERATION_AXES = [
  "style",
  "baseColor",
  "theme",
  "chart",
  "radius",
  "font",
] as const satisfies readonly (keyof DesignSystemGenerationContract)[];

export const DESIGN_SYSTEM_GENERATION_PACKAGE_PROJECTION_KIND =
  "generation-design-system" as const;
export const DESIGN_SYSTEM_GENERATION_PACKAGE_PROJECTION_COMPILER =
  "@cogo/design-presets/generation-package-projection" as const;
export const DESIGN_SYSTEM_GENERATION_PACKAGE_PROJECTION_COMPILER_VERSION =
  "1" as const;
export const DESIGN_SYSTEM_GENERATION_PACKAGE_PROJECTION_SCHEMA_VERSION =
  "cogo.design-system-generation-package-projection.v1" as const;

const ADMITTED_VALUES: Record<
  keyof DesignSystemGenerationContract,
  ReadonlySet<string>
> = {
  style: new Set(STYLES),
  baseColor: new Set(BASE_COLORS.map((entry) => entry.name)),
  theme: new Set(Object.keys(THEMES)),
  chart: new Set(Object.keys(CHART_PALETTES)),
  radius: new Set(RADII_LIST.map((entry) => entry.name)),
  font: new Set(Object.keys(FONTS)),
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Admit the exact six-axis generation selection against the canonical preset
 * registry. This function never fills a gap: lifecycle callers must supply
 * every axis from their server-side compiler before a proposal can exist.
 */
export function admitDesignSystemGenerationContract(
  value: unknown,
): DesignSystemGenerationContract {
  if (!isRecord(value)) {
    throw new Error(
      `DESIGN_SYSTEM_GENERATION_AXIS_AUTHORITY_MISSING: ${DESIGN_SYSTEM_GENERATION_AXES.join(",")}`,
    );
  }

  const missing = DESIGN_SYSTEM_GENERATION_AXES.filter(
    (axis) => typeof value[axis] !== "string" || value[axis].trim().length === 0,
  );
  if (missing.length > 0) {
    throw new Error(
      `DESIGN_SYSTEM_GENERATION_AXIS_AUTHORITY_MISSING: ${missing.join(",")}`,
    );
  }

  const extras = Object.keys(value)
    .filter(
      (key) =>
        !DESIGN_SYSTEM_GENERATION_AXES.includes(
          key as (typeof DESIGN_SYSTEM_GENERATION_AXES)[number],
        ),
    )
    .sort();
  if (extras.length > 0) {
    throw new Error(
      `DESIGN_SYSTEM_GENERATION_CONTRACT_UNEXPECTED_FIELDS: ${extras.join(",")}`,
    );
  }

  for (const axis of DESIGN_SYSTEM_GENERATION_AXES) {
    const selected = value[axis] as string;
    if (!ADMITTED_VALUES[axis].has(selected)) {
      throw new Error(
        `DESIGN_SYSTEM_GENERATION_AXIS_AUTHORITY_NOT_ADMITTED: ${axis}`,
      );
    }
  }

  return Object.fromEntries(
    DESIGN_SYSTEM_GENERATION_AXES.map((axis) => [axis, value[axis]]),
  ) as unknown as DesignSystemGenerationContract;
}

/** Canonical bytes addressed by the package's generation projection record. */
export function designSystemGenerationPackageProjectionContent(
  value: unknown,
): {
  schemaVersion: typeof DESIGN_SYSTEM_GENERATION_PACKAGE_PROJECTION_SCHEMA_VERSION;
  generationDesignSystem: DesignSystemGenerationContract;
} {
  return {
    schemaVersion: DESIGN_SYSTEM_GENERATION_PACKAGE_PROJECTION_SCHEMA_VERSION,
    generationDesignSystem: admitDesignSystemGenerationContract(value),
  };
}
